import React, { useEffect, useState } from 'react';
import { Mail, Linkedin, Menu, X } from 'lucide-react';
import '../styles/Home.css';

const Home = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      
      // Detect active section
      const sections = ['hero', 'about', 'portfolio', 'partners', 'team', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  const portfolioItems = [
    { name: 'Cryptool', logo: '/images/cryptool.png', url: 'https://cryptool.io/', active: true },
    { name: 'Beamswap', logo: '/images/beamswap.png', url: 'https://beamswap.io/', active: true },
    { name: 'Battleverse', logo: '/images/battleverse.png', url: 'https://battleverse.io/', active: true },
    { name: 'Plutonians', logo: '/images/plutonians.png', url: 'https://plutonians.tech/', active: true },
    { name: 'Mummy', logo: '/images/mummy.png', url: 'https://store.steampowered.com/app/4052670/Sands_Of_Avalon_Forge_Your_Legend/', active: true },
    { name: 'Metaoasis', logo: '/images/metaoasis.jpg', url: 'https://www.momoai.io/', active: true },
    { name: 'Moon', logo: '/images/moon.jpg', url: 'https://moon.ws/', active: true },
    { name: 'Algodex', logo: '/images/algodex.png', url: 'https://app.algodex.com/about', active: true },
    { name: 'Radom', logo: '/images/radom.png', url: 'https://www.radom.com/', active: true },
  ];

  const partners = [
    { name: 'Cov3st', logo: '/images/cov3st.jpg', url: null },
    { name: 'IYI', logo: '/images/iyi-real.jpg', url: 'https://iyi.io/' },
  ];

  const teamMembers = [
    {
      name: 'Ron',
      role: 'Partner & Investments Manager',
      image: '/images/ron.jpg',
      linkedin: 'https://www.linkedin.com/in/ron-zabel-48665278/',
      gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.8), rgba(5, 150, 105, 0.8))'
    },
    {
      name: 'Alain',
      role: 'Partner & Operations & Finance Manager',
      image: '/images/alain.jpg',
      linkedin: 'https://www.linkedin.com/in/alain-voogt-3853a290/',
      gradient: 'linear-gradient(135deg, rgba(148, 163, 184, 0.8), rgba(71, 85, 105, 0.8))'
    },
    {
      name: 'Jeffrey',
      role: 'Partner & Design & Communication Manager',
      image: '/images/jeffrey.jpg',
      linkedin: 'https://www.linkedin.com/in/jeffrey-van-dijk-68832927/',
      gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.8), rgba(14, 116, 144, 0.8))'
    },
    {
      name: 'Billah',
      role: 'Technical Coordinator',
      image: '/images/billah.jpg',
      linkedin: 'https://www.linkedin.com/in/billah26/',
      gradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.8), rgba(126, 34, 206, 0.8))'
    },
  ];

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo">
            <img src="/images/favicon.png" alt="GAP VC" />
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-links">
            <button onClick={() => scrollToSection('about')} className={activeSection === 'about' ? 'active' : ''}>
              About
            </button>
            <button onClick={() => scrollToSection('portfolio')} className={activeSection === 'portfolio' ? 'active' : ''}>
              Portfolio
            </button>
            <button onClick={() => scrollToSection('partners')} className={activeSection === 'partners' ? 'active' : ''}>
              Partners
            </button>
            <button onClick={() => scrollToSection('team')} className={activeSection === 'team' ? 'active' : ''}>
              Team
            </button>
            <button onClick={() => scrollToSection('contact')} className={activeSection === 'contact' ? 'active' : ''}>
              Contact
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <button onClick={() => scrollToSection('about')} className={activeSection === 'about' ? 'active' : ''}>
              About
            </button>
            <button onClick={() => scrollToSection('portfolio')} className={activeSection === 'portfolio' ? 'active' : ''}>
              Portfolio
            </button>
            <button onClick={() => scrollToSection('partners')} className={activeSection === 'partners' ? 'active' : ''}>
              Partners
            </button>
            <button onClick={() => scrollToSection('team')} className={activeSection === 'team' ? 'active' : ''}>
              Team
            </button>
            <button onClick={() => scrollToSection('contact')} className={activeSection === 'contact' ? 'active' : ''}>
              Contact
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-background" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/space-background.jpg)` }}>
          <div className="stars"></div>
          <div className="stars-2"></div>
          <div className="stars-3"></div>
        </div>
        <div className="hero-content">
          <div className="hero-logo-container">
            <img src="/images/gap-logo-original.png" alt="Green Access Partners V.C." className="hero-logo" />
            <div className="hero-glow"></div>
          </div>
          <h1 className="hero-tagline">
            <span className="tagline-highlight">Backing ambitious partners</span>
            <span className="tagline-text">to reach sustainable success</span>
          </h1>
          <p className="hero-subtitle">
            <span className="subtitle-highlight">Invest</span>, <span className="subtitle-highlight">Learn</span> and <span className="subtitle-highlight">Grow</span>
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-content">
            <p className="about-text">
              GAP VC is an investment firm focused on the crypto sector, backing entrepreneurs as they build and scale high-quality projects.
            </p>
            <p className="about-text">
              We focus on long-term value creation and sustainable growth, working with teams to strengthen their market position in an increasingly competitive and fast-evolving industry. With an experienced and entrepreneurial approach, we aim to support projects that have clear fundamentals and the potential to stand out.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Portfolio</h2>
            <div className="section-divider"></div>
          </div>
          <div className="portfolio-grid">
            {portfolioItems.map((item, index) => (
              <div
                key={index}
                className="portfolio-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {item.active ? (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="portfolio-link">
                    <div className="portfolio-logo-container">
                      <img src={item.logo} alt={item.name} className="portfolio-logo-img" />
                    </div>
                    <div className="portfolio-overlay">
                      <span className="visit-text">Visit Site</span>
                    </div>
                  </a>
                ) : (
                  <div className="portfolio-link portfolio-inactive">
                    <div className="portfolio-logo-container">
                      <img src={item.logo} alt={item.name} className="portfolio-logo-img" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="partners-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Partners</h2>
            <div className="section-divider"></div>
            <p className="section-subtitle">Building Trust and Transparency</p>
          </div>
          <div className="partners-grid">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="partner-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {partner.url ? (
                  <a href={partner.url} target="_blank" rel="noopener noreferrer" className="partner-link">
                    <div className="partner-logo-container">
                      <img src={partner.logo} alt={partner.name} className="partner-logo-img" />
                    </div>
                  </a>
                ) : (
                  <div className="partner-logo-container">
                    <img src={partner.logo} alt={partner.name} className="partner-logo-img" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Team</h2>
            <div className="section-divider"></div>
          </div>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="team-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="team-image-container">
                  <img src={member.image} alt={member.name} className="team-image" />
                  <div 
                    className="team-image-overlay"
                    style={{ background: member.gradient }}
                  >
                    <div className="radiating-lines"></div>
                  </div>
                </div>
                <div className="team-info">
                  <h3 className="team-name">{member.name}</h3>
                  <p className="team-role">{member.role}</p>
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="team-linkedin">
                      <Linkedin size={20} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Contact</h2>
            <div className="section-divider"></div>
          </div>
          <div className="contact-content">
            <p className="contact-text">
              Interested in learning more about our investment approach or discussing potential partnerships?
            </p>
            <div className="contact-info">
              <a href="mailto:greenaccesspartners@gmail.com" className="contact-email">
                <Mail size={28} />
                <span>greenaccesspartners@gmail.com</span>
              </a>
            </div>
            <div className="contact-social">
              <a href="https://www.linkedin.com/company/93370207" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                <Linkedin size={32} />
                <span>LinkedIn</span>
              </a>
              <a href="https://x.com/gap_vc?lang=en" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span>X (Twitter)</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-left">
              <p>&copy; {new Date().getFullYear()} Green Access Partners VC</p>
            </div>
            <div className="footer-right">
              <a href="mailto:greenaccesspartners@gmail.com" className="footer-link" aria-label="Email">
                <Mail size={24} />
              </a>
              <a href="https://x.com/gap_vc?lang=en" target="_blank" rel="noopener noreferrer" className="footer-link" aria-label="X (Twitter)">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/93370207" target="_blank" rel="noopener noreferrer" className="footer-link" aria-label="LinkedIn">
                <Linkedin size={24} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button - REMOVED */}
      {/* Removed to prevent covering footer social icons */}
    </div>
  );
};

export default Home;